import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PairingRecipes } from "@/components/RecipeBlock";
import { MoviePoster } from "@/components/MoviePoster";
import { Skeleton } from "@/components/ui/skeleton";
import { StarRating } from "@/components/StarRating";
import { Search, Utensils, Wine, ChevronDown, ChevronUp, Clapperboard, Heart, Share2, Clock, Flame, Lock, Trophy, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LIKED_KEY = "dm_liked_pairings";
const PAGE_SIZE = 12;
function getLiked() {
  try {
    return JSON.parse(localStorage.getItem(LIKED_KEY) || "[]");
  } catch {
    return [];
  }
}

export default function Community() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAuthed = user && user !== null;
  const [feed, setFeed] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("recent");
  const [expanded, setExpanded] = useState(null);
  const [liked, setLiked] = useState(getLiked());
  const [spotlight, setSpotlight] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  const loadFirst = async (q = "", s = sort) => {
    setFeed(null);
    const params = { sort: s, skip: 0, limit: PAGE_SIZE };
    if (q) params.search = q;
    const { data } = await api.get("/pairings/community", { params });
    setFeed(data);
    setHasMore(data.length === PAGE_SIZE);
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore || !feed) return;
    setLoadingMore(true);
    try {
      const params = { sort, skip: feed.length, limit: PAGE_SIZE };
      if (search) params.search = search;
      const { data } = await api.get("/pairings/community", { params });
      setFeed((f) => [...f, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadFirst(search, sort);
    // eslint-disable-next-line
  }, [sort]);

  useEffect(() => {
    const t = setTimeout(() => loadFirst(search, sort), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  useEffect(() => {
    api
      .get("/pairings/spotlight")
      .then((res) => setSpotlight(res.data.spotlight))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAuthed || !hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "300px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line
  }, [isAuthed, hasMore, feed, loadingMore, sort, search]);

  const toggleLike = async (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    const isLiked = liked.includes(p.id);
    try {
      const { data } = await api.post(`/pairings/${p.id}/${isLiked ? "unlike" : "like"}`);
      const nextLiked = isLiked ? liked.filter((x) => x !== p.id) : [...liked, p.id];
      setLiked(nextLiked);
      localStorage.setItem(LIKED_KEY, JSON.stringify(nextLiked));
      setFeed((f) => f.map((x) => (x.id === p.id ? { ...x, likes: data.likes } : x)));
      if (spotlight && spotlight.id === p.id) setSpotlight({ ...spotlight, likes: data.likes });
    } catch {
      toast.error("Could not update like");
    }
  };

  const share = async (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/p/${p.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard!");
    } catch {
      toast.message(url);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20 px-5 sm:px-8" data-testid="community-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 animate-fade-up">
          <span className="uppercase text-xs tracking-[0.3em] text-[#E50914]">
            Community Feed
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mt-2">
            What the world is watching & eating.
          </h1>
          <p className="text-white/50 mt-3">
            The latest public movie dinner pairings from the community.
          </p>
        </div>

        {isAuthed && spotlight && !search && (
          <div
            className="mb-8 rounded-3xl border border-[#F5B841]/30 bg-gradient-to-br from-[#F5B841]/10 to-transparent overflow-hidden animate-fade-up"
            data-testid="community-spotlight"
          >
            <div className="flex flex-col sm:flex-row">
              <MoviePoster
                title={spotlight.movie_title}
                className="h-48 sm:h-auto sm:w-56 shrink-0"
              />
              <div className="p-6 sm:p-8 flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy className="w-5 h-5 text-[#F5B841]" />
                  <span className="uppercase text-xs tracking-[0.25em] text-[#F5B841]">
                    Spotlight of the Week
                  </span>
                </div>
                <h2 className="font-display text-3xl font-black mb-2">{spotlight.movie_title}</h2>
                <p className="text-xs text-white/40 mb-4">by {spotlight.username}</p>
                <div className="space-y-1.5 text-sm text-white/75 mb-5">
                  <div className="flex items-center gap-2">
                    <Utensils className="w-4 h-4 text-[#E50914]" />
                    {spotlight.meal_name}
                  </div>
                  <div className="flex items-center gap-2">
                    <Wine className="w-4 h-4 text-[#F5B841]" />
                    {spotlight.drink_name}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1.5 text-sm text-[#E50914]">
                    <Heart className="w-4 h-4 fill-[#E50914]" /> {spotlight.likes} loves
                  </span>
                  <Button
                    onClick={() => navigate(`/p/${spotlight.id}`)}
                    data-testid="spotlight-view-btn"
                    className="rounded-full bg-[#F5B841] hover:bg-[#e0a72f] text-black h-9 px-6 text-sm"
                  >
                    View Pairing
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={`relative ${!isAuthed ? "min-h-[540px]" : ""}`}>
          {!isAuthed && (
            <div
              className="absolute inset-0 z-20 flex items-center justify-center p-4"
              data-testid="community-gate-overlay"
            >
              <div className="rounded-2xl border border-white/10 bg-black/80 backdrop-blur-xl p-8 sm:p-10 max-w-md text-center shadow-2xl">
                <div className="w-14 h-14 rounded-full bg-[#E50914]/15 flex items-center justify-center mx-auto mb-5">
                  <Lock className="w-7 h-7 text-[#E50914]" />
                </div>
                <h3 className="font-display text-2xl font-bold mb-2">
                  The community is members-only
                </h3>
                <p className="text-white/60 mb-6">
                  Create a free account to explore what the world is watching and eating,
                  and to share your own pairings.
                </p>
                <Button
                  onClick={() => navigate("/register")}
                  data-testid="community-signup-btn"
                  className="rounded-full bg-[#E50914] hover:bg-[#c00811] text-white h-11 px-8 w-full"
                >
                  Sign Up Free
                </Button>
                <button
                  onClick={() => navigate("/login")}
                  data-testid="community-gate-login"
                  className="text-sm text-white/60 hover:text-white transition-colors mt-4"
                >
                  Already have an account? Log in
                </button>
              </div>
            </div>
          )}

          <div className={!isAuthed ? "blur-md pointer-events-none select-none" : ""}>
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="community-search-input"
              placeholder="Search by movie title..."
              className="pl-12 h-12 bg-[#0d0d0d] border-white/10 rounded-full"
            />
          </div>
          <div className="flex items-center gap-1 bg-[#0d0d0d] border border-white/10 rounded-full p-1 self-start">
            <button
              onClick={() => setSort("recent")}
              data-testid="community-sort-recent"
              className={`flex items-center gap-1.5 px-4 h-10 rounded-full text-sm transition-colors ${
                sort === "recent" ? "bg-[#E50914] text-white" : "text-white/60 hover:text-white"
              }`}
            >
              <Clock className="w-4 h-4" /> Recent
            </button>
            <button
              onClick={() => setSort("top")}
              data-testid="community-sort-top"
              className={`flex items-center gap-1.5 px-4 h-10 rounded-full text-sm transition-colors ${
                sort === "top" ? "bg-[#E50914] text-white" : "text-white/60 hover:text-white"
              }`}
            >
              <Flame className="w-4 h-4" /> Most Loved
            </button>
          </div>
        </div>

        {feed === null && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-52 rounded-2xl bg-white/5" />
            ))}
          </div>
        )}

        {feed && feed.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-16 text-center" data-testid="community-empty">
            <Clapperboard className="w-14 h-14 text-[#E50914] mx-auto mb-5" />
            <h3 className="font-display text-2xl font-bold mb-2">
              {search ? "No public pairings match." : "No public pairings yet."}
            </h3>
            <p className="text-white/50">
              {search ? "Try a different movie." : "Be the first to share one!"}
            </p>
          </div>
        )}

        {feed && feed.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
            {feed.map((p) => {
              const isOpen = expanded === p.id;
              return (
                <div
                  key={p.id}
                  data-testid={`community-card-${p.id}`}
                  className={`rounded-2xl border bg-[#0d0d0d] overflow-hidden transition-all duration-300 ${
                    isOpen
                      ? "border-[#E50914]/40 md:col-span-2 lg:col-span-3"
                      : "border-white/5 hover:border-white/20"
                  }`}
                >
                  <button
                    onClick={() => setExpanded(isOpen ? null : p.id)}
                    data-testid={`community-toggle-${p.id}`}
                    className="w-full text-left"
                  >
                    <div className={`flex ${isOpen ? "flex-row" : "flex-col"}`}>
                      {!isOpen && (
                        <MoviePoster title={p.movie_title} className="h-40 w-full rounded-t-2xl" />
                      )}
                      <div className="p-6 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <span className="font-display text-xl font-bold leading-tight block">
                              {p.movie_title}
                            </span>
                            <p className="text-xs text-white/40 mt-1">by {p.username}</p>
                          </div>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-[#E50914] shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-white/40 shrink-0" />
                          )}
                        </div>
                        <div className="space-y-2 text-sm text-white/70 mt-4">
                          <div className="flex items-center gap-2">
                            <Utensils className="w-4 h-4 text-[#E50914]" />
                            <span className="truncate">{p.meal_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Wine className="w-4 h-4 text-[#F5B841]" />
                            <span className="truncate">{p.drink_name}</span>
                          </div>
                        </div>
                        {p.rating > 0 && (
                          <div className="mt-4">
                            <StarRating value={p.rating} readOnly size={14} />
                          </div>
                        )}
                      </div>
                    </div>
                  </button>

                  <div className="flex items-center gap-2 px-6 pb-4 -mt-2">
                    <button
                      onClick={(e) => toggleLike(e, p)}
                      data-testid={`community-like-${p.id}`}
                      className={`flex items-center gap-1.5 rounded-full px-3 h-9 text-sm transition-colors ${
                        liked.includes(p.id)
                          ? "bg-[#E50914]/20 text-[#E50914]"
                          : "bg-white/5 text-white/60 hover:text-white"
                      }`}
                    >
                      <Heart
                        className={`w-4 h-4 ${liked.includes(p.id) ? "fill-[#E50914]" : ""}`}
                      />
                      <span data-testid={`community-likes-count-${p.id}`}>{p.likes || 0}</span>
                    </button>
                    <button
                      onClick={(e) => share(e, p)}
                      data-testid={`community-share-${p.id}`}
                      className="flex items-center gap-1.5 rounded-full px-3 h-9 text-sm bg-white/5 text-white/60 hover:text-white transition-colors"
                    >
                      <Share2 className="w-4 h-4" /> Share
                    </button>
                  </div>

                  {isOpen && (
                    <div className="px-6 pb-6 animate-fade-up">
                      {p.theme_description && (
                        <p className="text-base font-light leading-relaxed text-white/85 mb-5">
                          {p.theme_description}
                        </p>
                      )}
                      <PairingRecipes pairing={p} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {isAuthed && feed && feed.length > 0 && (
          <div ref={sentinelRef} className="flex justify-center py-10" data-testid="community-sentinel">
            {loadingMore && <Loader2 className="w-6 h-6 text-[#E50914] animate-spin" />}
            {!hasMore && !loadingMore && (
              <p className="text-sm text-white/30" data-testid="community-end">
                You've reached the end of the reel.
              </p>
            )}
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
}
